import { Autocomplete, Button, Divider, Grid, Paper, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useStatisticsContext } from 'src/shared/contexts/statisticsContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { getCountMembersByAgeAndSex } from 'src/shared/services/statisticsService';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useFetchSnapshots } from '../hooks/useFetchSnapshots';
import { SnapshotTimeResponse } from '../models/snapshotTimeResponse';
import { AgeAndGenderDistribution } from '../models/ageAndGenderDistribution';
import { AgeAndGenderDistributionSearchFilter } from '../models/ageAndGenderDistributionSearchFilter';
import { AgeAndGenderDistributionDataGrid } from '../components/AgeAndGenderDistributionDataGrid';
import { StatisticsAgeDistribution } from '../components/statisticsAgeDistribution';

export const StatisticsAgeAndGenderDistributionPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  // Delar association/district med övriga statistik-sidor och får auto-förval om användaren
  // bara hör till en förening/ett distrikt — central logik i StatisticsContextProvider.
  const { association, setAssociation, district, setDistrict } = useStatisticsContext();
  const { spans } = useFetchSnapshots();

  const [dataList, setDataList] = useState<AgeAndGenderDistribution>();
  const [loading, setLoading] = useState<boolean>(false);

  const schema: yup.Schema<AgeAndGenderDistributionSearchFilter> = yup.object().shape({
    localAss_id: yup.number().optional(),
    countyAss_id: yup.number().optional(),
    snapshotTime_id: yup.number().optional(),
    customAges: yup.string().optional(),
    expireDateStart: yup.string().optional().nullable(),
    expireDateEnd: yup.string().optional().nullable(),
    expireDate: yup.string().optional().nullable(),
    payStatus: yup.number().optional(),
    ageCalcDate: yup.string().optional().nullable(),
  });

  const formMethods = useForm<AgeAndGenderDistributionSearchFilter>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      localAss_id: undefined,
      countyAss_id: undefined,
      snapshotTime_id: undefined,
      customAges: '',
      expireDateStart: '',
      expireDate: '',
      expireDateEnd: '',
      payStatus: 0,
      ageCalcDate: '',
    },
  });

  const { handleSubmit, watch, setValue } = formMethods;

  // Synka form-värden med context-scope (auto-förval eller manuellt val på annan stats-sida).
  useEffect(() => {
    setValue('localAss_id', association?.localAss_id || undefined);
  }, [association, setValue]);
  useEffect(() => {
    setValue('countyAss_id', district?.countyAss_id || undefined);
  }, [district, setValue]);

  const snapshotTimeId = watch('snapshotTime_id');

  const selectedSnapshot = spans.find((o) => o.snapshotTime_id === snapshotTimeId) ?? null;

  const handleFromDateChange = (_e, newValue) => {
    if (newValue) {
      setValue('snapshotTime_id', newValue.snapshotTime_id);
    } else {
      setValue('snapshotTime_id', undefined);
    }
  };

  const fetchData = useCallback(
    async (data: AgeAndGenderDistributionSearchFilter) => {
      setLoading(true);

      const dataToPost: AgeAndGenderDistributionSearchFilter = {
        localAss_id: data.localAss_id ? data.localAss_id : undefined,
        countyAss_id: data.countyAss_id ? data.countyAss_id : undefined,
        snapshotTime_id: data.snapshotTime_id ? data.snapshotTime_id : undefined,
        customAges: data.customAges ? data.customAges : undefined,
        expireDateStart: data.expireDateStart ? DateHelper.getDate(data.expireDateStart) : undefined,
        expireDate: data.expireDate ? DateHelper.getDate(data.expireDate) : undefined,
        expireDateEnd: data.expireDateEnd ? DateHelper.getDate(data.expireDateEnd) : undefined,
        payStatus: data.payStatus ? data.payStatus : undefined,
        ageCalcDate: data.ageCalcDate ? DateHelper.getDate(data.ageCalcDate) : undefined,
      };

      try {
        const res = await getCountMembersByAgeAndSex(dataToPost);
        if (isMounted()) setDataList(res.data);
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        setLoading(false);
      }
    },
    [isMounted],
  );

  const paymentStatuses = [
    {
      id: 1,
      description: t('statistics:ageAndGenderDistribution.paid'),
    },
    {
      id: 2,
      description: t('statistics:ageAndGenderDistribution.unpaid'),
    },
  ];

  const isOptionEqualToValue = (option: SnapshotTimeResponse, value: SnapshotTimeResponse) => option.snapshotTime_id === value.snapshotTime_id;

  const formId = 'search-member-gender-id';
  return (
    <FormProvider {...formMethods}>
      <form id={formId} onSubmit={handleSubmit(fetchData)}>
        <Grid container spacing={3}>
          <Grid item xs>
            <MainContentHeader title={t('statistics:report.ageAndGenderDistribution')} />
          </Grid>
          <Grid
            item
            xs='auto'
            sx={{
              pt: 2,
              pl: 2,
              display: { sm: 'flex' },

              '& .MuiAutocomplete-inputRoot': {
                backgroundColor: 'white',
              },
            }}
          >
            <AssociationAndDistrictSelect
              districtMb={2}
              hideDistrict={currentUser?.districts?.length === 0}
              association={association}
              district={district}
              associationCallback={(val) => {
                setValue('localAss_id', val?.localAss_id || undefined);
                setAssociation(val || undefined);
              }}
              districtCallback={(val) => {
                setValue('countyAss_id', val?.countyAss_id || undefined);
                setDistrict(val || undefined);
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} mt={3}>
          <Paper sx={{ p: 2 }}>
            <Grid container>
              <LoadingOverlay loading={loading} />
              <Grid container spacing={3} mb={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete<SnapshotTimeResponse>
                    value={selectedSnapshot}
                    isOptionEqualToValue={isOptionEqualToValue}
                    getOptionLabel={(option) => DateHelper.getDate(option.snapshotTime) || ''}
                    options={spans}
                    onChange={handleFromDateChange}
                    aria-required
                    renderOption={(props, option) => (
                      <li {...props} key={option.snapshotTime_id}>
                        {DateHelper.getDate(option.snapshotTime)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('statistics:ageAndGenderDistribution.now')}
                        fullWidth
                        label={t('statistics:ageAndGenderDistribution.time')}
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ControlledDatePicker name='expireDateStart' label={t('statistics:ageAndGenderDistribution.expireDateFrom')} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ControlledDatePicker name='expireDateEnd' label={t('statistics:ageAndGenderDistribution.expireDateTo')} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ControlledDatePicker name='ageCalcDate' label={t('statistics:ageAndGenderDistribution.ageCalcDate')} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <ControlledDatePicker name='expireDate' label={t('statistics:ageAndGenderDistribution.breakDatePaymentStatus')} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <ControlledAutocompleteDropdown
                    componentWidth='100%'
                    nameProp='description'
                    name='paymentStatus'
                    label={t('statistics:ageAndGenderDistribution.paymentStatus')}
                    placeholder={t('common:button.choose')}
                    options={paymentStatuses || []}
                    callback={(selectedOption) => {
                      if (selectedOption && selectedOption.id === 1) {
                        setValue('payStatus', selectedOption.id);
                      } else if (selectedOption && selectedOption.id === 2) {
                        setValue('payStatus', selectedOption?.id);
                      } else {
                        setValue('payStatus', 0);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ControlledTextField name='customAges' label={t('statistics:ageAndGenderDistribution.customAges')} />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                {/* <Grid item>
                  <Button onClick={() => ConsoleHelper.log()} variant='outlined'>
                    {t('statistics:ageAndGenderDistribution.export')}
                  </Button>
                </Grid> */}
                <Grid item>
                  <Button form={formId} type='submit' variant='contained'>
                    {t('common:button.search')}
                  </Button>
                </Grid>
              </Grid>
              {dataList && (
                <Grid item xs={12}>
                  {dataList.sexItems && dataList.sexItems.length > 0 && (
                    <>
                      <Divider sx={{ my: 2, mx: -2 }} />
                      <StatisticsAgeDistribution dataList={dataList.sexItems} />
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <AgeAndGenderDistributionDataGrid dataList={dataList} />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </form>
    </FormProvider>
  );
};
